import React, { useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { forgotPassword } from '../services/authService';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    if (!email) {
      setError('Email address is required.');
      setIsLoading(false);
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Enter a valid email address.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await forgotPassword({ email });
      setSuccessMessage(response.message || 'If your email is registered, a reset link is on the way.');
      setEmail('');
    } catch (err: unknown) {
      console.error('Forgot password error:', err);
      const message = err instanceof Error ? err.message : 'An error occurred. Try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 2, md: 6 } }}>
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src="/images/DIRTcarLogo.png" alt="DIRTcar" style={{ height: 48, width: 'auto' }} />
          </Box>
          <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
            Forgot password
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Enter the email on your account and a reset link will be sent.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="email"
                autoFocus
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={isLoading} fullWidth>
                {isLoading ? 'Sending' : 'Send reset link'}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2.5 }}>
            <MuiLink component={RouterLink} to="/login" variant="body2" underline="hover">
              Back to sign in
            </MuiLink>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
